import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { DOCUMENT_TYPE } from '@/constants/document'
import { logger } from '@/services/logger'
import {
  type GetDocumentsResponse,
  type GetUploadsResponse,
  getUploadFiles,
  getDocuments,
  setDocument,
  deleteDocument
} from '@/services/companyDocuments'
import { useEmployeeStore } from './employee'

export const useDocumentsStore = defineStore('documents', () => {
  const allDocuments = ref<GetDocumentsResponse>()
  const allFilesUploaded = ref<GetUploadsResponse>()

  const travelPolicy = computed(() =>
    allDocuments.value?.find((document) => document.type === DOCUMENT_TYPE.TRAVEL_POLICY)
  )

  const travelPolicyUpload = computed(() =>
    allFilesUploaded.value?.find((upload) => upload.type === DOCUMENT_TYPE.TRAVEL_POLICY)
  )

  const resolveCompanyId = async (): Promise<string> => {
    const employeeStore = useEmployeeStore()

    if (!employeeStore.employee?.companyId) {
      await employeeStore.setEmployee()
    }

    const companyId = employeeStore.employee?.companyId
    if (!companyId) {
      throw new Error('No company Id')
    }

    return companyId
  }

  const getCompanyDocument = async () => {
    try {
      const companyId = await resolveCompanyId()
      const response = await getDocuments(companyId)

      allDocuments.value = response.data
      return response
    } catch (error) {
      logger.error(error)
      throw error
    }
  }

  const getCompanyUpload = async () => {
    try {
      const companyId = await resolveCompanyId()
      const response = await getUploadFiles(companyId)

      allFilesUploaded.value = response.data
      return response
    } catch (error) {
      logger.error(error)
      throw error
    }
  }

  const uploadCompanyDocument = async (file: File) => {
    try {
      const companyId = await resolveCompanyId()
      return await setDocument(companyId, file, DOCUMENT_TYPE.TRAVEL_POLICY)
    } catch (error) {
      logger.error(error)
      throw error
    }
  }

  const deleteCompanyDocument = async (documentId: string) => {
    try {
      const companyId = await resolveCompanyId()
      await deleteDocument(companyId, documentId)
      allDocuments.value = allDocuments.value?.filter((doc) => doc.id !== documentId)
    } catch (error) {
      logger.error(error)
      throw error
    }
  }

  return {
    allDocuments,
    allFilesUploaded,
    travelPolicy,
    travelPolicyUpload,
    getCompanyDocument,
    getCompanyUpload,
    uploadCompanyDocument,
    deleteCompanyDocument
  }
})
